import React, { useMemo, useState } from "react";
import { runCompliance } from "../utils/compliance";
import { SKELETON, GROUP_ORDER, mergeSkeleton } from "./complianceParams";

/**
 * ComplianceCard
 * -------------------------------------------------------------------------
 * Thin view over the pure engine: give it a resolved DCR param file, the
 * achieved values and the project facts, and it renders verdicts across the
 * full master approval checklist (see complianceParams.js).
 *
 * Presentation is deliberately soft — tinted pills rather than hard outlines,
 * unencoded rows recede instead of shouting, sub-groups break the list into
 * readable chunks. What is NOT softened is the verdict itself: per the master
 * list's severity gate, any demolition or stop-work FAIL caps the whole result
 * to red. It is never averaged into a friendly score.
 *
 * Props: paramFile, achieved, facts, zoneConfidence
 * ------------------------------------------------------------------------- */

const T = {
  text: "var(--text,#e6ebf5)",
  dim: "var(--text-dim,#9aa7c0)",
  border: "var(--border,#3a4456)",
  surface: "var(--surface-2,#1a2234)",
  accent: "#3987e5",
  ok: "#199e70",
  warn: "#c98500",
  err: "#d9534f",
};

// soft = tinted background + matching ink, no hard border
const STATUS = {
  pass:             { label: "Pass",        ink: "#4ec99a", bg: "rgba(25,158,112,0.16)" },
  fail:             { label: "Fail",        ink: "#f08b87", bg: "rgba(217,83,79,0.18)" },
  "not-encoded":    { label: "Not encoded", ink: "#8b96ad", bg: "rgba(139,150,173,0.12)" },
  "not-applicable": { label: "N/A",         ink: "#8b96ad", bg: "rgba(139,150,173,0.10)" },
  "needs-parse":    { label: "Needs plan",  ink: "#e0ac4a", bg: "rgba(201,133,0,0.16)" },
  "needs-input":    { label: "Needs input", ink: "#e0ac4a", bg: "rgba(201,133,0,0.16)" },
};

const SEV_LABEL = {
  demolition: "Demolition risk",
  "stop-work": "Stop-work risk",
  "oc-denial": "OC denial",
  compounding: "Compounding fee",
};
// severities that cap the whole result to red
const CRITICAL = new Set(["demolition", "stop-work"]);

const CONF = {
  ocr: { label: "OCR — verify", ink: "#f08b87" },
  "needs-review": { label: "review", ink: "#e0ac4a" },
  auto: { label: "auto", ink: "#8b96ad" },
};

const CATS = [
  { key: "exterior",  icon: "📐", title: "Exterior",  sub: "vs massing envelope" },
  { key: "interior",  icon: "🏠", title: "Interior",  sub: "vs parsed floor plan" },
  { key: "amenities", icon: "🚰", title: "Amenities", sub: "counts & services" },
];

export default function ComplianceCard({ paramFile, achieved, facts, zoneConfidence }) {
  const hasInputs = !!(paramFile || achieved || facts);
  const awaitingDcr = !paramFile && hasInputs;
  const file = useMemo(() => mergeSkeleton(paramFile), [paramFile]);
  const ach = achieved || {};
  const f = facts || {};

  const results = useMemo(
    () => runCompliance(file, ach, f, { zoneConfidence }),
    [file, ach, f, zoneConfidence]
  );
  const [open, setOpen] = useState({ exterior: true, interior: false, amenities: false });
  const [showAll, setShowAll] = useState(false);

  const rows = [
    ...results.categories.exterior,
    ...results.categories.interior,
    ...results.categories.amenities,
  ];
  const fails = rows.filter((r) => r.status === "fail");
  const passes = rows.filter((r) => r.status === "pass");
  const critical = fails.filter((r) => CRITICAL.has(r.severity));
  const checked = fails.length + passes.length;
  const hasOcr = rows.some((r) => r.confidence === "ocr");

  return (
    <div style={S.card}>
      <Header
        name={results.regulationName}
        version={results.version}
        awaitingDcr={awaitingDcr}
        fails={fails}
        critical={critical}
        passes={passes.length}
        checked={checked}
        total={rows.length}
      />

      {zoneConfidence && zoneConfidence !== "gis" && zoneConfidence !== "confirmed" && (
        <Note tone="warn">Zone {zoneConfidence} — confirm the land-use zone; the whole verdict depends on it.</Note>
      )}
      {hasOcr && (
        <Note tone="err">Some values were read from a scanned document (OCR) — verify against the source before relying on them.</Note>
      )}

      <label style={S.toggle}>
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} style={{ accentColor: T.accent }} />
        Show parameters with no rule encoded yet
      </label>

      {CATS.map((cat) => (
        <Category
          key={cat.key}
          cat={cat}
          rows={results.categories[cat.key] || []}
          defs={(file.parameters || {})[cat.key] || {}}
          isOpen={open[cat.key]}
          onToggle={() => setOpen((o) => ({ ...o, [cat.key]: !o[cat.key] }))}
          showAll={showAll}
        />
      ))}

      <div style={S.foot}>
        Screening-grade — briefs the architect and a formal sanction review. Not a sanctioned calculation.
      </div>
    </div>
  );
}

function Header({ name, version, awaitingDcr, fails, critical, passes, checked, total }) {
  // Severity gate: critical failures set the tone, never a friendly average.
  const tone = critical.length ? "err" : fails.length ? "warn" : checked ? "ok" : "dim";
  const headline = critical.length
    ? `${critical.length} critical ${critical.length === 1 ? "issue" : "issues"}`
    : fails.length
    ? `${fails.length} ${fails.length === 1 ? "issue" : "issues"}`
    : checked
    ? "All checked parameters pass"
    : "Nothing checked yet";
  const ink = { err: "#f08b87", warn: "#e0ac4a", ok: "#4ec99a", dim: T.dim }[tone];
  const bg = { err: "rgba(217,83,79,0.13)", warn: "rgba(201,133,0,0.12)", ok: "rgba(25,158,112,0.12)", dim: "rgba(139,150,173,0.08)" }[tone];

  return (
    <div style={{ ...S.hero, background: bg }}>
      <div style={S.heroTop}>
        <div>
          <div style={{ ...S.heroHeadline, color: ink }}>{headline}</div>
          <div style={S.heroMeta}>
            {name || "—"}{version ? ` · v${version}` : ""}
          </div>
        </div>
        {awaitingDcr && <span style={S.chip}>no DCR yet</span>}
      </div>

      {critical.length > 0 && (
        <div style={S.criticalList}>
          {critical.slice(0, 3).map((r) => (
            <div key={r.id} style={S.criticalItem}>
              <span style={{ color: "#f08b87" }}>●</span> {r.label} — {SEV_LABEL[r.severity]}
            </div>
          ))}
        </div>
      )}

      <div style={S.progressWrap}>
        <div style={S.progressTrack}>
          <div style={{ width: `${total ? (checked / total) * 100 : 0}%`, height: "100%", background: T.accent, borderRadius: 999 }} />
        </div>
        <span style={S.progressText}>{checked} of {total} checkable</span>
      </div>
    </div>
  );
}

function Category({ cat, rows, defs, isOpen, onToggle, showAll }) {
  const visible = showAll ? rows : rows.filter((r) => r.status !== "not-encoded");
  const fails = rows.filter((r) => r.status === "fail").length;
  const checked = rows.filter((r) => r.status === "pass" || r.status === "fail").length;

  // bucket by the skeleton's group label
  const groups = {};
  visible.forEach((r) => {
    const g = defs[r.id]?.group || "Other";
    (groups[g] = groups[g] || []).push(r);
  });
  const order = [...(GROUP_ORDER[cat.key] || []), "Other"].filter((g) => groups[g]?.length);

  return (
    <section style={S.section}>
      <button style={S.sectionHead} onClick={onToggle}>
        <span style={S.caret}>{isOpen ? "▾" : "▸"}</span>
        <span aria-hidden="true">{cat.icon}</span>
        <span style={S.sectionTitle}>{cat.title}</span>
        <span style={S.sectionSub}>{cat.sub}</span>
        <span style={{ ...S.sectionCount, color: fails ? "#f08b87" : T.dim }}>
          {checked ? `${checked - fails}/${checked}` : `${rows.length} params`}
        </span>
      </button>

      {isOpen && (
        <div style={{ marginTop: 8 }}>
          {order.length === 0 && (
            <div style={S.empty}>
              Nothing resolved here yet. Tick “show parameters with no rule encoded” to see the full checklist.
            </div>
          )}
          {order.map((g) => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div style={S.groupHead}>{g}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {groups[g].map((r) => <Row key={r.id} v={r} def={defs[r.id]} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Row({ v, def }) {
  const st = STATUS[v.status] || STATUS["not-encoded"];
  const cand = v.required?.candidates || [];
  const conf = (v.status === "pass" || v.status === "fail") ? CONF[v.confidence] : null;
  const muted = v.status === "not-encoded" || v.status === "not-applicable";
  const isCritical = v.status === "fail" && CRITICAL.has(v.severity);

  return (
    <div style={{
      ...S.row,
      opacity: muted ? 0.62 : 1,
      background: isCritical ? "rgba(217,83,79,0.07)" : "transparent",
      borderLeft: isCritical ? "2px solid rgba(217,83,79,0.55)" : "2px solid transparent",
    }}>
      <div style={S.rowTop}>
        <span style={S.rowLabel}>{v.label}</span>
        <span style={S.rowBadges}>
          {conf && <span style={{ ...S.conf, color: conf.ink }}>{conf.label}</span>}
          <span style={{ ...S.pill, color: st.ink, background: st.bg }}>{st.label}</span>
        </span>
      </div>

      <div style={S.rowSub}>
        {v.required?.value != null && (
          <span>
            needs {fmt(v.required.value, v.unit)}
            {cand.length > 1 && (
              <span style={{ color: T.dim }}> ({cand.map((c) => fmt(c.value, v.unit)).join(" / ")} → governs)</span>
            )}
          </span>
        )}
        {v.achieved?.value != null && (
          <span style={{ marginLeft: v.required?.value != null ? 8 : 0 }}>
            {v.required?.value != null ? "· " : ""}you have {fmt(v.achieved.value, v.unit)}
          </span>
        )}
        {v.status === "fail" && <span style={{ marginLeft: 8, color: st.ink }}>· {SEV_LABEL[v.severity]}</span>}
      </div>

      {(v.message || def?.note) && v.status !== "pass" && (
        <div style={S.rowMsg}>
          {v.status === "not-encoded" ? (def?.note || v.message) : v.message}
          {v.required?.governingClauseId && <span style={{ color: T.accent }}> · {v.required.governingClauseId}</span>}
        </div>
      )}
    </div>
  );
}

const Note = ({ tone, children }) => (
  <div style={{
    ...S.note,
    color: tone === "err" ? "#f08b87" : "#e0ac4a",
    background: tone === "err" ? "rgba(217,83,79,0.10)" : "rgba(201,133,0,0.10)",
  }}>{children}</div>
);

function fmt(val, unit) {
  if (typeof val === "boolean") return val ? "required" : "—";
  return `${val}${unit ? (unit === "%" ? "%" : " " + unit) : ""}`;
}

const S = {
  card: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, color: T.text, fontSize: 13, display: "flex", flexDirection: "column", gap: 12 },
  hero: { borderRadius: 11, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 9 },
  heroTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  heroHeadline: { fontSize: 15, fontWeight: 600, letterSpacing: -0.2 },
  heroMeta: { fontSize: 10.5, color: T.dim, marginTop: 3, lineHeight: 1.35 },
  chip: { fontSize: 9.5, color: "#e0ac4a", background: "rgba(201,133,0,0.15)", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" },
  criticalList: { display: "flex", flexDirection: "column", gap: 3 },
  criticalItem: { fontSize: 11, color: T.text, opacity: 0.9 },
  progressWrap: { display: "flex", alignItems: "center", gap: 8 },
  progressTrack: { flex: 1, height: 5, background: "rgba(255,255,255,0.09)", borderRadius: 999, overflow: "hidden" },
  progressText: { fontSize: 9.5, color: T.dim, whiteSpace: "nowrap" },
  toggle: { display: "flex", alignItems: "center", gap: 7, fontSize: 10.5, color: T.dim, cursor: "pointer", userSelect: "none" },
  section: { borderTop: `1px solid ${T.border}`, paddingTop: 10 },
  sectionHead: { display: "flex", alignItems: "center", gap: 7, width: "100%", background: "none", border: "none", color: T.text, cursor: "pointer", padding: 0, textAlign: "left" },
  caret: { color: T.dim, width: 10, fontSize: 11 },
  sectionTitle: { fontSize: 12.5, fontWeight: 600 },
  sectionSub: { fontSize: 10, color: T.dim },
  sectionCount: { marginLeft: "auto", fontSize: 11 },
  groupHead: { fontSize: 9.5, color: T.dim, textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 4px 2px" },
  row: { display: "flex", flexDirection: "column", gap: 2, padding: "6px 8px", borderRadius: 8 },
  rowTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  rowLabel: { fontSize: 12 },
  rowBadges: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  pill: { fontSize: 9.5, fontWeight: 600, borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap" },
  conf: { fontSize: 8.5, fontWeight: 600, letterSpacing: 0.3, whiteSpace: "nowrap", opacity: 0.9 },
  rowSub: { fontSize: 11, color: T.text, opacity: 0.85 },
  rowMsg: { fontSize: 10, color: T.dim, lineHeight: 1.45 },
  note: { fontSize: 10.5, borderRadius: 9, padding: "8px 10px", lineHeight: 1.5 },
  empty: { fontSize: 11, color: T.dim, lineHeight: 1.5, padding: "2px 2px 6px" },
  foot: { fontSize: 9.5, color: T.dim, borderTop: `1px solid ${T.border}`, paddingTop: 9, lineHeight: 1.5, fontStyle: "italic" },
};
